import { useState, useRef, useEffect } from "react";
import { dateNumeric } from "../utils/formatDate";
import { useMarketStatus, lastSessionLabel } from "../utils/marketStatus";
import { T, UI, BTN } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { TR } from "../constants/data";
import { fetchMetrics } from "../utils/finnhub";
import { useFxRates } from "../utils/fxRates";
import { pct } from "../utils/formatters";
import { useIsMobile } from "../utils/hooks";
import StockDetailPanel from "./StockDetailPanel";
import SegmentPicker from "./SegmentPicker";
import MarketsCard from "./MarketsCard";
import AddStock from "./AddStock";
import LTR from "./LTR";
import WatchlistSection from "./WatchlistSection";
import { usePurchaseRates, shekelSince, PURCHASE_RATE_LABEL, TODAY_RATE_LABEL, PRICE_ONLY_LABEL, FX_EFFECT_LABEL } from "../utils/purchaseFx";
import AllocationRing from "./AllocationRing";
import Icon from "./Icon";
import Money, { Amount } from "./Money";
import { modalScrim, modalPanel, MODAL_PAD_X } from "./Modal";
import DualMoney, { withShekel } from "./DualMoney";
import HoldingCard from "./HoldingCard";
import InfoTip from "./InfoTip";
import HeroChange from "./HeroChange";

// ─── Market Sentiment — designed empty state ─────────────────────────────────
// There is no live sentiment source yet (the old gauge was a local
// random-walk: a number with no source). Zero-fabrication rule: no numeric
// value until stage 4.2 wires real per-asset sentiment.
// The hero card (26/10/2026, mockup 4).
const HERO_TWO_COLUMNS = 900;   // px of the card itself
const HERO_CHANGE = 20;         // "מאז רכישה" and the day · 15 until now

// The width of an element, kept current.
function useWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // The card's outer width, padding included.
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.borderBoxSize?.[0]?.inlineSize ?? e.target.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

const SAMPLE_CSV = `symbol,quantity,buy_price,buy_date
AAPL,10,182.50,2024-01-15
NVDA,5,620.00,2024-02-20
MSFT,8,415.00,2024-03-10
TSLA,15,175.00,2024-03-25
AMZN,12,180.00,2024-04-01
GOOGL,6,172.00,2024-05-12`;

function detectBroker(headers) {
  const h = headers.map(x => x.trim().toLowerCase());
  if (h.some(x => x === "תאריך" || x === "סימול" || x === "פעולה")) return "מיטב / אקסלנס";
  if (h.includes("trade date") || h.includes("ticker") || h.includes("transaction")) return "Interactive Brokers";
  if (h.includes("date") && h.includes("symbol") && h.includes("action")) return "eToro / קובץ רגיל";
  if (h.includes("date") && h.includes("symbol")) return "TASE / פורמט כללי";
  return "פורמט כללי";
}

function normalizeRow(headers, values) {
  const h = headers.map(x => x.trim().toLowerCase());
  const get = (...keys) => {
    for (const k of keys) { const i = h.indexOf(k); if (i !== -1 && values[i] !== undefined) return values[i].trim(); }
    return "";
  };
  const symbol = get("symbol","סימול","ticker").toUpperCase();
  const action = get("action","פעולה","transaction").toUpperCase();
  const shares = parseFloat(get("shares","כמות","quantity")) || 0;
  const price  = parseFloat(get("price","מחיר","cost","buy_price")) || 0;
  const date   = get("date","תאריך","trade date","buy_date");
  return { symbol, action, shares, price, date };
}

function parseCSVText(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error("הקובץ ריק או לא חוקי");
  const headers = lines[0].split(",");
  const broker  = detectBroker(headers);
  const hasActionCol = headers.map(x => x.trim().toLowerCase()).some(h =>
    h === "action" || h === "פעולה" || h === "transaction"
  );
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",");
    const row = normalizeRow(headers, values);
    if (!row.symbol || row.shares <= 0 || row.price <= 0) continue;
    const isBuy = !hasActionCol ||
      row.action.includes("BUY") || row.action.includes("קנ") ||
      row.action === "BOUGHT" || row.action === "קנייה";
    if (!isBuy) continue;
    rows.push(row);
  }
  if (!rows.length) throw new Error("לא נמצאו עסקאות קנייה חוקיות בקובץ");
  return { rows, broker };
}

function CSVImportModal({ onClose, onImport }) {
  const [step,        setStep]      = useState("upload");
  const [dragging,    setDragging]  = useState(false);
  const [rows,        setRows]      = useState([]);
  const [broker,      setBroker]    = useState("");
  const [error,       setError]     = useState("");
  const [importCount, setImport]    = useState(0);
  const fileRef = useRef(null);

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type:"text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "sample_trades.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { setError("יש להעלות קובץ CSV בלבד (.csv)"); return; }
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { rows: r, broker: b } = parseCSVText(e.target.result);
        setRows(r); setBroker(b); setStep("preview");
      } catch (err) { setError(err.message); }
    };
    reader.readAsText(file, "utf-8");
  };

  const confirmImport = () => {
    const grouped = {};
    rows.forEach(r => {
      if (!grouped[r.symbol]) grouped[r.symbol] = { totalCost: 0, totalShares: 0, firstDate: r.date || null };
      grouped[r.symbol].totalCost   += r.price * r.shares;
      grouped[r.symbol].totalShares += r.shares;
    });
    let count = 0;
    Object.entries(grouped).forEach(([symbol, d]) => {
      onImport({
        symbol,
        shares:   d.totalShares,
        buyPrice: +(d.totalCost / d.totalShares).toFixed(2),
        buyDate:  d.firstDate || null,
      });
      count++;
    });
    setImport(count); setStep("success");
  };

  return (
    <div dir="rtl" style={modalScrim(300)} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalPanel({ width: 500, pad: false })}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:`14px ${MODAL_PAD_X}px`, borderBottom:`1px solid ${UI.line.hairline}`, flexShrink:0 }}>
          <span style={{ display:"inline-flex", alignItems:"center", gap:8, fontWeight:800, fontSize:15, color:UI.text.primary }}><Icon name="folderOpen" size={16} color={UI.text.secondary}/> ייבוא עסקאות מברוקר</span>
          <button onClick={onClose} aria-label="סגור" style={{ display:"inline-flex", background:"none", border:"none", color:UI.text.secondary, cursor:"pointer", lineHeight:1, padding:2 }}><Icon name="x" size={18} color={UI.text.secondary}/></button>
        </div>

        <div style={{ padding:`16px ${MODAL_PAD_X}px 22px`, display:"flex", flexDirection:"column", gap:14 }}>

          {/* ── UPLOAD step ── */}
          {step === "upload" && <>
            <div>
              <div style={{ ...TYPE.headingSub, color:UI.accent.base, marginBottom:8 }}>פורמטים נתמכים</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {["מיטב","אקסלנס","Interactive Brokers","eToro","TASE"].map(b => (
                  <span key={b} style={{ fontSize:13, fontWeight:700, color:UI.text.muted, background:UI.fill.subtle, border:`1px solid ${UI.line.hairline}`, borderRadius:4, padding:"3px 10px" }}>{b}</span>
                ))}
              </div>
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              style={{ border:`2px dashed ${dragging ? UI.interactive : UI.line.hairline}`, borderRadius:6, padding:"40px 22px", textAlign:"center", cursor:"pointer", background:dragging ? UI.accent.soft : UI.surface.raised, transition:"all .2s" }}>
              <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><Icon name="folder" size={34} color={UI.text.secondary}/></div>
              <div style={{ fontSize:13, fontWeight:700, color:UI.text.primary, marginBottom:4 }}>גרור קובץ CSV לכאן</div>
              <div style={{ fontSize:13, color:UI.text.secondary }}>או לחץ לבחור קובץ</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])}/>
            </div>

            {error && (
              <div style={{ display:"flex", alignItems:"center", gap:6, background:UI.cautionSoft, border:`1px solid ${UI.cautionSoft}`, borderRadius:6, padding:"10px 14px", fontSize:12, color:UI.caution }}><Icon name="warning" size={13} color={UI.caution}/> {error}</div>
            )}

            <div style={{ background:UI.accent.soft, border:`1px solid ${UI.accent.soft}`, borderRadius:6, padding:"12px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:UI.text.primary, marginBottom:2 }}>לא בטוח מה הפורמט?</div>
                <div style={{ fontSize:11, color:UI.text.muted }}>הורד קובץ דוגמה עם 6 עסקאות לדוגמה</div>
              </div>
              <button onClick={downloadSample} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:6, padding:"8px 14px", color:UI.text.secondary, fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap" }}>
                <Icon name="download" size={14} color={UI.text.secondary}/> הורד דוגמה
              </button>
            </div>

            <div style={{ background:UI.surface.raised, border:`1px solid ${UI.line.hairline}`, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"12px 14px" }}>
              <div style={{ fontSize:13, fontWeight:800, color:UI.text.secondary, marginBottom:8, textTransform:"uppercase" }}>פורמטים מקובלים של עמודות</div>
              {[
                { f:"פשוט (ברירת מחדל)",  cols:"symbol · quantity · buy_price · buy_date" },
                { f:"eToro / רגיל", cols:"Date · Symbol · Action (BUY) · Shares · Price" },
                { f:"עברית",        cols:"תאריך · סימול · פעולה (קנייה) · כמות · מחיר" },
                { f:"Interactive Brokers", cols:"Trade Date · Ticker · Transaction · Quantity · Cost" },
              ].map(r => (
                <div key={r.f} style={{ display:"flex", gap:10, marginBottom:5, alignItems:"flex-start" }}>
                  <span style={{ fontSize:13, fontWeight:700, color:UI.text.secondary, flexShrink:0, minWidth:90 }}>{r.f}</span>
                  <span style={{ fontSize:13, color:UI.text.secondary, lineHeight:1.5 }}>{r.cols}</span>
                </div>
              ))}
            </div>
          </>}

          {/* ── PREVIEW step ── */}
          {step === "preview" && <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, fontWeight:800, color:UI.text.secondary }}><Icon name="check" size={13} color={UI.text.secondary}/> זוהה: {broker}</div>
                <div style={{ fontSize:13, color:UI.text.muted }}>{rows.length} עסקאות קנייה נמצאו</div>
              </div>
              <button onClick={() => { setStep("upload"); setRows([]); setError(""); }} style={{ background:"none", border:`1px solid ${UI.line.hairline}`, borderRadius:5, padding:"6px 10px", color:UI.text.muted, fontSize:13, cursor:"pointer" }}>← חזור</button>
            </div>

            <div style={{ border:`1px solid ${UI.line.hairline}`, borderRadius:4, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1.2fr 70px 80px 90px", background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"8px 12px", gap:8 }}>
                {["סמל","כמות","מחיר","תאריך"].map(h => (
                  <span key={h} style={{ fontSize:13, fontWeight:800, color:UI.text.secondary }}>{h}</span>
                ))}
              </div>
              <div style={{ maxHeight:220, overflowY:"auto" }}>
                {rows.map((r, i) => (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"1.2fr 70px 80px 90px", padding:"8px 12px", borderBottom:`1px solid ${UI.line.hairline}`, gap:8, background:i%2 ? UI.surface.raised : UI.surface.raised }}>
                    <span style={{ fontWeight:800, color:UI.text.primary, fontSize:12 }}>{r.symbol}</span>
                    <span style={{ color:UI.text.primary, fontSize:12 }}>{r.shares}</span>
                    <span style={{ color:UI.text.primary, fontSize:12 }}><Amount value={Number(r.price)} currency="USD" decimals={2} size={12} weight={500}/></span>
                    <span style={{ color:UI.text.secondary, fontSize:13 }}>{dateNumeric(r.date) ? <LTR className="si-num">{dateNumeric(r.date)}</LTR> : r.date}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background:UI.accent.soft, border:`1px solid ${UI.accent.soft}`, borderRadius:5, padding:"10px 12px", fontSize:13, color:UI.text.muted, lineHeight:1.65 }}>
              <span style={{ fontWeight:800, color:UI.text.primary }}>הערה: </span>
              מניות עם מספר עסקאות קנייה יחושבו לפי מחיר ממוצע משוקלל. מניות הקיימות בתיק לא תתווספנה שוב.
            </div>

            <button onClick={confirmImport} className="si-btn-gold" style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, background:"transparent", border:`1px solid ${UI.accent.base}`, borderRadius:6, padding:"14px 0", fontSize:14, fontWeight:700, color:UI.accent.base, cursor:"pointer" }}>
              <Icon name="check" size={16} color={UI.surface.page}/> אשר ייבוא · <LTR>{rows.length}</LTR> עסקאות
            </button>
          </>}

          {/* ── SUCCESS step ── */}
          {step === "success" && (
            <div style={{ textAlign:"center", padding:"26px 16px" }}>
              <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}><Icon name="checkCircle" size={52} color={UI.text.secondary}/></div>
              <div style={{ fontSize:18, fontWeight:800, color:UI.text.secondary, marginBottom:8 }}>יובאו {importCount} מניות בהצלחה!</div>
              <div style={{ fontSize:12, color:UI.text.muted, marginBottom:24, lineHeight:1.75 }}>
                ההחזקות נוספו לתיק שלך.<br/>בדוק את הרשימה לאישור הנתונים.
              </div>
              <button onClick={onClose} style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:6, padding:"12px 30px", fontSize:14, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
                סגור
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Portfolio Tab ────────────────────────────────────────────────────────────
export default function PortfolioTab({ portfolio, cur, onAdd, onRemove, onOpenAlerts, lang, watch }) {
  const market = useMarketStatus();
  const t = TR[lang] || TR.he;
  const [selected,     setSelected]     = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  // The one question asked right after a holding is added (stage 3). CSV
  // imports do not ask — a batch of twenty questions is a wall, and the home
  // banner covers holdings that were never asked.
  const [askFor,       setAskFor]       = useState(null);
  const isMobile   = useIsMobile();

  const [liveMetrics, setLiveMetrics] = useState({});
  // Live USD/ILS for the hero ILS line — same feed as the ticker.
  // null = unavailable → the line is not rendered (no stale fallback).
  const ilsRateHero = useFxRates().ILS?.rate || null;
  useEffect(() => {
    portfolio.forEach((s, i) => {
      setTimeout(() => {
        fetchMetrics(s.symbol).then(d => {
          if (!d?.metric) return;
          setLiveMetrics(prev => ({
            ...prev,
            [s.symbol]: {
              pe:  d.metric.peBasicExclExtraTTM  ?? d.metric.peTTM,
              div: d.metric.dividendYieldIndicatedAnnual,
            },
          }));
        });
      }, i * 400);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Finnhub or nothing. The static PE_DATA table that used to sit behind
  // these held 2024 figures for nine companies and showed them unlabelled.
  const getPE  = sym => liveMetrics[sym]?.pe  ?? null;
  const getDiv = sym => liveMetrics[sym]?.div ?? null;

  // A holding without a quote (delisted, acquired, renamed) has no current
  // value and no measurable gain or loss — so it is outside every sum here,
  // and the hero card says how many were left out. Its buy price is not a
  // stand-in: that number would look exactly like a live position.
  const quoted     = portfolio.filter(s => s.quote !== "none");
  const unquoted   = portfolio.length - quoted.length;
  const totalValue = quoted.reduce((a, s) => a + s.currentPrice * s.shares, 0);
  const totalCost  = quoted.reduce((a, s) => a + s.buyPrice     * s.shares, 0);
  // In shekels, the gain is measured against the cost at the Bank of Israel
  // rate of each purchase day (sprint 13, 5.2 · utils/purchaseFx.js), and
  // only that way (bug round, 17/10/2026): until the rates are in, a
  // placeholder; without them, "—" and the reason. Never today's rate.
  const purchaseRates = usePurchaseRates(portfolio);
  // In every display currency: the shekel line under dollars, euros or
  // pounds uses it too (sprint 15, 1.4).
  const ilsSince   = shekelSince(quoted, purchaseRates, ilsRateHero);
  const ilsReady   = ilsSince?.state === "ready" ? ilsSince : null;
  const totalPnL   = cur === "ILS" && ilsReady ? ilsReady.ils / ilsRateHero : totalValue - totalCost;
  const totalPct   = cur === "ILS" && ilsReady ? ilsReady.pct : totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
  const [sinceOpen, setSinceOpen] = useState(false);
  // What "מאז רכישה" is measured by: the price only, and · when shekels are on
  // the card · at which rate (sprint 13, 5.2). Behind the ⓘ since sprint 16.
  const shekelsShown = cur !== "USD" || !!ilsRateHero;
  // Euros and pounds are the dollar gain at today's rate; the shekels are at
  // the purchase-day rate in every display currency.
  const otherCur = cur !== "USD" && cur !== "ILS";
  const sinceNote = lang === "en"
    ? ["Price only, without dividends", otherCur && "at today's rate", shekelsShown && (cur === "ILS" ? "" : "₪ ") + "at the purchase-day Bank of Israel rate"].filter(Boolean).join(" · ")
    : [PRICE_ONLY_LABEL, otherCur && TODAY_RATE_LABEL, shekelsShown && (cur === "ILS" ? "" : "₪ ") + PURCHASE_RATE_LABEL].filter(Boolean).join(" · ");

  // Real daily change (quotes with prevClose only) + allocation weights for
  // the hero card. Same math as the home screen — one source of truth: quotes.
  const dailyChangeTotal = portfolio.reduce((a, s) =>
    s.prevClose > 0 ? a + (s.currentPrice - s.prevClose) * s.shares : a, 0);
  const prevTotal    = totalValue - dailyChangeTotal;
  const dailyPctTotal = prevTotal > 0 ? (dailyChangeTotal / prevTotal) * 100 : 0;
  // The hero card's own width decides its layout (not the window's: the
  // desktop has a side menu).
  const heroRef = useRef(null);
  const heroWide = useWidth(heroRef) >= HERO_TWO_COLUMNS;
  const allocRows = [...quoted]
    .map(s => ({ symbol: s.symbol, value: s.currentPrice * s.shares }))
    .filter(h => h.value > 0)
    .sort((a, b) => b.value - a.value)
    .map(h => ({ ...h, weight: totalValue > 0 ? (h.value / totalValue) * 100 : 0 }));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* ── Hero portfolio card — THE anchor of this screen ─────────
          The same role the home tab's value card plays: one dark surface for
          the eye to rest on, or every card floats. .si-anchor supplies the
          ground, the lift, and a redefined token ramp for its whole subtree,
          so nothing inside needs to know it is on a dark surface. */}
      <div ref={heroRef} className="si-anchor" style={{
        borderRadius: T.radius.xxl,
        padding: isMobile ? "26px 22px 22px" : "32px 30px 26px",
        border: `1px solid ${UI.line.hairline}`,
      }}>
        <div className="si-savanna-band" aria-hidden="true"/>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
          {/* Sprint 17: "שווי התיק", as on the home card, and no letter
              spacing · tracking pulls Hebrew letters apart. */}
          <span style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 700 }}>{lang === "en" ? t.totalPortfolio : "שווי התיק"}</span>
        </div>
        {/* Two columns from a 900px-wide card (26/10/2026, mockup 4): the
            figures on the right, the ring at 1.5× on the left, where a wide
            screen used to leave the card empty. Narrower, one column. */}
        <div style={heroWide
          ? { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 32, alignItems: "center" }
          : { display: "flex", flexDirection: "column" }}>
        <div style={{ minWidth: 0 }}>
        {/* The dominant number in the card — split symbol/int/fraction */}
        {/* The dollars, and the shekels under them (sprint 15, DualMoney · the
            muted "₪… · לפי שער" line that sat here is that line now). */}
        <DualMoney usd={totalValue} cur={cur} size={isMobile ? 44 : 56}>
          <Money value={totalValue} cur={cur} size={isMobile ? 44 : 56} weight={700} className="si-num" whole color={UI.text.primary} style={{ lineHeight: 1.05, letterSpacing: "-0.02em" }}/>
        </DualMoney>
        {cur !== "USD" && <div style={{ marginTop: 4 }}><Money value={totalValue} cur="USD" size={12} weight={500} whole color={UI.text.secondary}/></div>}
        {/* Since purchase and the day, as two units of the same shape
            (HeroChange · sprint 17, the same component as the home card).
            20 instead of 15 (26/10/2026): about 30% larger. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20 }}>
          <HeroChange cur={cur} dollar={totalPnL} pct={totalPct} size={HERO_CHANGE}
            ils={ilsReady ? ilsReady.ils : null} ilsNote={PURCHASE_RATE_LABEL}
            ilsSince={ilsSince} ilsCaption={FX_EFFECT_LABEL}
            label={t.sinceInvestment}
            info={<InfoTip text={sinceNote} label={lang === "en" ? "How this is measured" : "על מה מחושב מאז רכישה"} open={sinceOpen} onToggle={setSinceOpen}/>}/>
          {sinceOpen && <div role="note" style={{ fontSize: 12, color: UI.text.secondary, lineHeight: 1.55, marginTop: -6 }}>{withShekel(sinceNote)}</div>}
          {/* Daily change · quotes with prevClose only, never invented */}
          {portfolio.some(s => s.prevClose > 0) && (
            <HeroChange cur={cur} dollar={dailyChangeTotal} pct={dailyPctTotal} size={HERO_CHANGE}
              label={market.open ? "שינוי יומי" : lastSessionLabel(market)}/>
          )}
        </div>
        </div>

        {/* Allocation · real position weights, as a ring (sprint 16, stage 3):
            the seven largest in hues of their own, the rest as "אחרות". */}
        {/* The margin under it keeps the legend's last row off the savanna
            band (88px tall; the card reserves 72). */}
        {allocRows.length > 0 && (
          <div data-hero-ring style={heroWide
            ? { display: "flex", justifyContent: "center", alignItems: "center", borderInlineStart: `1px solid ${UI.line.hairline}`, paddingInlineStart: 32, alignSelf: "stretch", marginBottom: 16 }
            : { marginTop: 18, marginBottom: 16 }}>
            <AllocationRing rows={allocRows} holdings={portfolio.length} size={heroWide ? 210 : isMobile ? 120 : 140} stroke={heroWide ? 27 : isMobile ? 16 : 18}/>
          </div>
        )}
        </div>

        {unquoted > 0 && (
          <div style={{ fontSize: 12.5, color: UI.text.muted, marginTop: 12 }}>
            {unquoted === 1 ? "החזקה אחת ללא ציטוט אינה כלולה בסכום" : <><LTR className="si-num">{unquoted}</LTR> החזקות ללא ציטוט אינן כלולות בסכום</>}
          </div>
        )}
      </div>

      <MarketsCard/>

      {/* "מה אם" is a screen of its own since sprint 13 (menu, and "בדוק
          בתרחיש" on the company page). The collapsed card that stood here
          was removed. */}

      {portfolio.map(s => {
        const noQuote   = s.quote === "none";
        const hasBuyPrice = s.buyPrice > 0 && !noQuote;
        // Shekels at the purchase-day rate only (5.2 · bug round, 17/10/2026).
        const oneSince  = hasBuyPrice ? shekelSince([s], purchaseRates, ilsRateHero) : null;
        const ilsOne    = oneSince?.state === "ready" ? oneSince : null;
        const pnl       = hasBuyPrice ? (cur === "ILS" && ilsOne ? ilsOne.ils / ilsRateHero : (s.currentPrice - s.buyPrice) * s.shares) : null;
        const pp        = hasBuyPrice ? (cur === "ILS" && ilsOne ? ilsOne.pct.toFixed(2) : pct(s.buyPrice, s.currentPrice)) : null;
        const open      = selected === s.symbol;
        const peVal     = getPE(s.symbol);
        const divVal    = getDiv(s.symbol);
        return (
          <div key={s.symbol} style={{ display: "flex", flexDirection: "column" }}>
            <HoldingCard s={s} cur={cur} totalValue={totalValue} pnl={pnl} pnlPct={pp} pnlIls={ilsOne ? ilsOne.ils : null}
              pnlIlsSince={oneSince} rateNote={PURCHASE_RATE_LABEL}
              open={open} onToggle={() => setSelected(open ? null : s.symbol)}/>
            {open && (
              <StockDetailPanel
                symbol={s.symbol} name={s.name} currentPrice={s.currentPrice} prevClose={s.prevClose} buyPrice={s.buyPrice}
                cur={cur}
                onRemove={() => onRemove(s.symbol)} removeLabel={`הסר ${s.symbol} מהתיק`}
                onClose={() => setSelected(null)} onOpenAlerts={onOpenAlerts}
                shares={s.shares} pe={peVal} divYield={divVal} noQuote={noQuote}
                lang={lang}
              />
            )}
          </div>
        );
      })}
      {/* Import button row */}
      <button onClick={() => setShowCsvModal(true)}
        style={{ ...BTN.secondary, background:UI.fill.subtle, border:`1px solid ${UI.line.hairline}`, padding:"12px 14px", color:UI.text.primary, fontSize:13, width:"100%" }}>
        <Icon name="folderOpen" size={16} color={UI.text.primary}/> ייבוא מברוקר (CSV)
      </button>

      <AddStock onAdd={async (pos) => { const added = await onAdd(pos); if (added) setAskFor(pos.symbol); }} type="portfolio"/>

      <WatchlistSection watch={watch} cur={cur}/>

      {askFor && (
        <SegmentPicker symbol={askFor} onDone={() => setAskFor(null)} onClose={() => setAskFor(null)}/>
      )}

      {showCsvModal && (
        <CSVImportModal onClose={() => setShowCsvModal(false)} onImport={(pos) => { onAdd(pos); }}/>
      )}
    </div>
  );
}
